import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, CreditCard, BookOpen, Check, Gift, ChevronDown, Flame, AlertTriangle, Heart } from 'lucide-react';
import { trackAddToCart } from '../utils/pixel';
interface OfferSectionProps {
  id?: string;
  showUrgency?: boolean;
  showCountdown?: boolean;
  showGuarantee?: boolean;
  showPayment?: boolean;
  ctaText?: string;
}
export function OfferSection({
  id,
  showUrgency = true,
  showCountdown = false,
  showGuarantee = true,
  showPayment = true,
  ctaText = 'ACCEDER AHORA POR $7.77 →'
}: OfferSectionProps) {
  const [viewers, setViewers] = useState(42);
  useEffect(() => {
    if (showUrgency) {
      const viewerInterval = setInterval(() => {
        setViewers(prev => prev + Math.floor(Math.random() * 3) - 1);
      }, 5000);
      return () => clearInterval(viewerInterval);
    }
  }, [showUrgency]);
  return <section id={id} className="py-12 px-4 max-w-[600px] mx-auto text-center relative" data-id="element-232">
      <div className="relative z-10" data-id="element-233">
        {/* Product Image */}
        <div className="mb-8 flex justify-center relative" data-id="element-234">
          {/* Animated glow halo */}
          <motion.div animate={{
          scale: [1, 1.18, 1],
          opacity: [0.35, 0.65, 0.35]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 bg-gradient-to-b from-[#1B3A6B]/25 via-[#B83A52]/15 to-[#1B3A6B]/25 rounded-3xl blur-3xl" data-id="element-235" />
          <motion.img src="https://cdn.magicpatterns.com/uploads/b6mXwauyzTQX5WrXdT3wdS/1apoyo.png" alt="El Apoyo Incondicional — Guía Digital" animate={{
          y: [0, -12, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative w-[240px] h-auto drop-shadow-2xl" data-id="element-236" />
        </div>

        {/* Value Stack */}
        <div className="bg-white rounded-2xl p-6 mb-6 border border-[#1B3A6B]/20 shadow-navy-soft relative overflow-hidden" data-id="element-237">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#1B3A6B] to-[#B83A52]" data-id="element-238"></div>

          <div className="space-y-3 mb-6 text-left text-sm border-b border-[#E2E8F0] pb-6" data-id="element-239">
            <div className="flex justify-between items-center" data-id="element-240">
              <span className="text-[#1C1C2E] font-medium flex items-center gap-2" data-id="element-241">
                <BookOpen className="w-4 h-4 text-[#1B3A6B]" data-id="element-242" /> Guía "El Apoyo
                Incondicional"
              </span>
              <span className="text-[#4A4A6A] line-through" data-id="element-243">$37</span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-1 leading-none mb-6" data-id="element-244">
            <div className="text-sm text-[#4A4A6A] font-medium mb-1" data-id="element-245">
              VALOR TOTAL: <span className="line-through" data-id="element-246">$37.77</span>
            </div>
            <div className="font-serif font-bold text-[48px] text-[#1B3A6B] tracking-tight mt-2" data-id="element-247">
              $7.77
            </div>
            <div className="text-xs text-[#8B8BAA] font-medium" data-id="element-248">
              Pago único · Acceso de por vida
            </div>
          </div>

          <div className="flex justify-center gap-4 pt-6 border-t border-[#E2E8F0] text-[#4A4A6A] text-xs font-bold" data-id="element-249">
            <div className="flex items-center gap-1.5" data-id="element-250">
              <Shield className="w-4 h-4 text-[#1B3A6B]" data-id="element-251" /> Garantía 30 Días
            </div>
            <div className="flex items-center gap-1.5" data-id="element-252">
              <CreditCard className="w-4 h-4 text-[#1B3A6B]" data-id="element-253" /> Pago Seguro
            </div>
          </div>
        </div>

        {showUrgency && <div className="flex flex-col gap-3 mb-6" data-id="element-254">
            <div className="bg-[#FDF0F3] text-[#B83A52] text-sm p-3 rounded-lg border border-[#B83A52]/20 flex items-center justify-center gap-2" data-id="element-255">
              <span className="w-2 h-2 bg-[#B83A52] rounded-full animate-pulse" data-id="element-256"></span>
              <strong data-id="element-257">{viewers} familias</strong> están viendo esta página
            </div>
          </div>}

        <motion.a href="https://oriammarketing.com/cart/45555442090182:1" onClick={() => trackAddToCart()} animate={{
        scale: [1, 1.03, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="relative block w-full overflow-hidden bg-[#B83A52] text-white font-bold py-5 px-6 rounded-xl text-xl uppercase tracking-wide shadow-[0_8px_30px_rgba(184,58,82,0.5)] mb-6 flex items-center justify-center gap-2 cursor-pointer" data-id="element-258">
          {/* Shimmer sweep */}
          <motion.span animate={{
          x: ['-100%', '200%']
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 1
        }} className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[-20deg] pointer-events-none" data-id="element-259" />
          <span className="relative z-10 flex items-center gap-2" data-id="element-260">
            {ctaText}
            <motion.span animate={{
            x: [0, 5, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="text-xl" data-id="element-261">
              →
            </motion.span>
          </span>
        </motion.a>

        {showPayment && <div className="flex flex-col items-center gap-2 text-center" data-id="element-262">
            <div className="flex gap-3 opacity-60 grayscale hover:grayscale-0 transition-all" data-id="element-263">
              {['VISA', 'MC', 'AMEX', 'PAYPAL'].map(card => <span key={card} className="text-[10px] border border-[#E2E8F0] px-2 py-1 rounded text-[#4A4A6A] font-bold bg-white" data-id="element-264">
                  {card}
                </span>)}
            </div>
          </div>}
      </div>
    </section>;
}